import type { BoxProps } from '@talos/kyoko';
import {
  AutocompleteResults,
  Box,
  Button,
  FormControlSizes,
  Icon,
  IconButton,
  IconName,
  Input,
  MixpanelEvent,
  MixpanelEventProperty,
  Stack,
  Text,
  useMixpanel,
  useSearchSelect,
  VStack,
  type FuseSearchResult,
  type SortFilterOverride,
} from '@talos/kyoko';
import { Result } from '@talos/kyoko/src/components/Form/AutocompleteDropdown/styles';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AutoSizer from 'react-virtualized-auto-sizer';
import { AdminPaths } from '../../NavigationSidebar/hooks/getNavPath';
import { AggregationListItem } from './components/AggregationListItem';
import { PresetListGroupHeader } from './components/PresetListGroupHeader';
import {
  createSyntheticPresetSearchResult,
  GROUP_AGGREGATIONS,
  GROUP_DEFAULT_AGGREGATION,
  ITEM_HEIGHT,
  presetRowEntriesRepresentSameSelection,
  type PresetRowEntry,
} from './presetListModel';
import { useMarketSelectorPresets } from './useMarketSelectorPresets';

/**
 * The MarketSelectorPresetsList component renders the list of presets provided from the MarketSelectorContext.
 *
 * Default aggregation (when set) is pinned above a single virtualized list so only one scrollbar is used.
 */
export const MarketSelectorPresetsList = ({
  forceShowSearch,
  ...props
}: {
  forceShowSearch?: boolean;
} & BoxProps) => {
  const mixpanel = useMixpanel();
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    marketSelectorAPI,
    defaultAggregationId,
    setDefaultAggregationId,
    favoriteAggregationsSet,
    favoriteAggregation,
    customPresetItemsByID,
    showNoAggregations,
    listEntries,
    selectedPreset,
    selectedListEntry,
    getLabel,
  } = useMarketSelectorPresets();

  const { selectPreset, narrowModeConfig } = marketSelectorAPI;
  const { setShowMarketSelectorBlotter } = narrowModeConfig;

  const [searching, setSearching] = useState(false);

  const navigate = useNavigate();

  const handleSelectPreset = useCallback(
    (presetID: string | undefined) => {
      if (!presetID) {
        return;
      }
      setShowMarketSelectorBlotter(true);

      const customPresetItem = customPresetItemsByID.get(presetID);
      if (customPresetItem) {
        customPresetItem.onClick(marketSelectorAPI);
      } else {
        selectPreset(presetID);
        mixpanel.track(MixpanelEvent.SelectAggregation);
      }
    },
    [selectPreset, customPresetItemsByID, marketSelectorAPI, mixpanel, setShowMarketSelectorBlotter]
  );

  const handleDefaultAggregationChange = useCallback(
    (presetID: string | undefined) => {
      if (presetID === undefined) {
        setDefaultAggregationId(undefined);
        mixpanel.track(MixpanelEvent.ClearDefaultAggregation);
        return;
      }
      setDefaultAggregationId(presetID);
      mixpanel.track(MixpanelEvent.SetDefaultAggregation, {
        [MixpanelEventProperty.Checked]: true,
        [MixpanelEventProperty.ID]: presetID,
      });
    },
    [setDefaultAggregationId, mixpanel]
  );

  const pinnedDefaultEntry = useMemo(
    (): PresetRowEntry | undefined => listEntries.find(e => e.kind === 'aggregation' && e.placement === 'defaultPin'),
    [listEntries]
  );

  const scrollListEntries = useMemo(
    () => listEntries.filter(e => !(e.kind === 'aggregation' && e.placement === 'defaultPin')),
    [listEntries]
  );

  const pinnedSearchResult = useMemo((): FuseSearchResult<PresetRowEntry> | undefined => {
    if (pinnedDefaultEntry == null) {
      return undefined;
    }
    return createSyntheticPresetSearchResult(pinnedDefaultEntry, getLabel(pinnedDefaultEntry));
  }, [pinnedDefaultEntry, getLabel]);

  const sortFilterOverride = useCallback<SortFilterOverride<PresetRowEntry>>((itemList, searchString) => {
    const q = searchString.trim();
    if (!q) {
      return itemList;
    }
    return itemList.filter(si => si.lowerLabel.includes(q));
  }, []);

  const hideAggregationsToolbar = searching || forceShowSearch === true;

  const handlePressAggregationSearch = useCallback(() => {
    setSearching(true);
    mixpanel.track(MixpanelEvent.SearchForAggregation);
  }, [mixpanel]);

  const autocomplete = useSearchSelect<PresetRowEntry>({
    selection: selectedListEntry ?? undefined,
    items: scrollListEntries,
    getLabel,
    sortFilterOverride,
    scrollSelectionIntoViewIndex: (scrollItems, item) =>
      scrollItems.findIndex(i => {
        if (i != null && typeof i === 'object' && 'items' in i) {
          return false;
        }
        return presetRowEntriesRepresentSameSelection(i as PresetRowEntry, item as PresetRowEntry);
      }),
    inputRef,
    onChange: entry => handleSelectPreset(entry?.presetId),
    inputValueChangeOnItemSelection: marketSelectorAPI.isNarrow ? 'clear' : 'keep',
    closeDropdownOnItemSelection: false,
    initialInputValue: '',
    initialSortByLabel: false,
  });

  const renderResult = useCallback(
    (searchResult: FuseSearchResult<PresetRowEntry>, disabled: boolean) => {
      const row = searchResult.item.item;
      const presetID = row.presetId;
      const isFavorite = favoriteAggregationsSet.has(presetID);
      return (
        <AggregationListItem
          favorite={isFavorite}
          onFavoriteClicked={() => {
            favoriteAggregation(presetID, !isFavorite);
            mixpanel.track(MixpanelEvent.FavoriteAggregation);
          }}
          searchResult={searchResult}
          selection={selectedPreset}
          customPresetItemsByID={customPresetItemsByID}
          defaultAggregationId={defaultAggregationId}
          onDefaultAggregationChange={handleDefaultAggregationChange}
        />
      );
    },
    [
      selectedPreset,
      favoriteAggregationsSet,
      favoriteAggregation,
      customPresetItemsByID,
      mixpanel,
      defaultAggregationId,
      handleDefaultAggregationChange,
    ]
  );

  const menuProps = autocomplete.getMenuProps();

  return (
    <Stack
      fontSize="fontSizeSm"
      overflow="hidden"
      flex="1"
      position="relative"
      background="gray.040"
      data-testid="market-selector-panel-presets-list"
      {...props}
      {...menuProps}
    >
      <Box
        minHeight="40px"
        display={searching || forceShowSearch ? 'flex' : 'none'}
        alignItems="center"
        background="backgroundInput"
        pr="spacing12"
        borderBottom="1px solid var(--backgroundDivider)"
      >
        <Input
          {...autocomplete.getInputProps({
            ref: inputRef,
            style: {
              display: searching || forceShowSearch ? 'flex' : 'none',
              border: 'none',
              borderRadius: '0',
              background: 'transparent',
            },
          })}
          placeholder="Search..."
          prefix={<Icon icon={IconName.Search} />}
          data-testid="market-selector-panel-presets-search"
        />
        <IconButton
          icon={IconName.Close}
          size={FormControlSizes.Tiny}
          dim
          onClick={() => {
            autocomplete.setInputValue('');
            setSearching(false);
          }}
        />
      </Box>
      {showNoAggregations ? (
        <VStack w="100%" flex={1}>
          <Text fontSize="fontSizeSmall" p="spacing12">
            No relevant aggregations found
          </Text>
          <Button
            startIcon={IconName.Exit}
            size={FormControlSizes.Tiny}
            onClick={() => navigate(AdminPaths.TradingAggregations())}
          >
            Manage Aggregations
          </Button>
        </VStack>
      ) : (
        <Stack flex={1} minHeight={0} overflow="hidden">
          {pinnedDefaultEntry != null && pinnedSearchResult != null && (
            <Stack flex="none">
              <PresetListGroupHeader
                groupTitle={GROUP_DEFAULT_AGGREGATION}
                hideAggregationsToolbar={hideAggregationsToolbar}
                onPressSearch={handlePressAggregationSearch}
              />
              <Result
                as="div"
                isHighlighted={false}
                isSelected={selectedPreset === pinnedDefaultEntry.presetId}
                disabled={false}
                selectionIndicator
                role="option"
                aria-selected={selectedPreset === pinnedDefaultEntry.presetId}
                onClick={() => handleSelectPreset(pinnedDefaultEntry.presetId)}
              >
                <AggregationListItem
                  favorite={favoriteAggregationsSet.has(pinnedDefaultEntry.presetId)}
                  onFavoriteClicked={() => {
                    const id = pinnedDefaultEntry.presetId;
                    favoriteAggregation(id, !favoriteAggregationsSet.has(id));
                    mixpanel.track(MixpanelEvent.FavoriteAggregation);
                  }}
                  searchResult={pinnedSearchResult}
                  selection={selectedPreset}
                  customPresetItemsByID={customPresetItemsByID}
                  defaultAggregationId={defaultAggregationId}
                  onDefaultAggregationChange={handleDefaultAggregationChange}
                />
              </Result>
            </Stack>
          )}
          <Stack flex={1} minHeight={0} overflow="hidden">
            <Stack flex="none">
              <PresetListGroupHeader
                groupTitle={GROUP_AGGREGATIONS}
                itemCount={autocomplete.searchResults.length}
                hideAggregationsToolbar={hideAggregationsToolbar}
                onPressSearch={handlePressAggregationSearch}
              />
            </Stack>
            <Box flex={1} minHeight={0} overflow="hidden" w="100%">
              <AutoSizer disableWidth>
                {({ height }) =>
                  height > 0 ? (
                    <AutocompleteResults
                      {...autocomplete}
                      equalityChecker={(a, b) => b != null && presetRowEntriesRepresentSameSelection(a, b)}
                      maxHeight={height}
                      groupMaxHeight={height}
                      itemSize={ITEM_HEIGHT}
                      renderResult={renderResult}
                      showBorderSelectionIndicator
                      isOpen // always true
                    />
                  ) : null
                }
              </AutoSizer>
            </Box>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};
